(function () {
    'use strict';
    /*
     * Copyright (c) 2014-25 In Hand Health.
     * All rights reserved.
     */

    angular
        .module('ihh-common-web')
        .service('Utilities', Utilities);

    Utilities.$inject = ['$modalStack', '$modal', '$location', '$window', 'UserSvc'];

    /**
     * @ngdoc service
     * @memberof ihh-common-web
     * @name Utilities
     * @description This service contains various commonly used utility functions.
     * Do not use <code>new</code> to create an instance. See the example below for how to use the service in a controller.
     * @example
     * // Declaring the utilities service in a controller.
     * myModule.controller('MyCtrl', ['Utilities', ..., function(Utilities, ...) {
     *    var someVariable = ...
     *
     *    $scope.foundANumber = Utilities.isNumber(someVariable);
     * }
     */
    function Utilities($modalStack, $modal, $location, $window, UserSvc) {

        var svc = this;
        /**
         * Returns whether the passed argument is a number. Taken from a StackOverflow answer.
         * @name isNumber
         * @ngdoc method
         * @memberOf ihh-common-web.Utilities
         * @param {Object} n the object to check
         * @returns {boolean} true if the object is a number; false otherwise
         */
        this.isNumber = function (n) {
            return !isNaN(parseFloat(n)) && isFinite(n);
        };

        /**
         * If the input is defined, tries to create a date from it.
         * @name makeDate
         * @ngdoc method
         * @memberOf ihh-common-web.Utilities
         * @param {type} input the date-formatted input
         * @returns {Date} the date from the input
         */
        this.makeDate = function (input) {
            if (input) {
                return new Date(input);
            }
        };

        /**
         * Determines whether an object is a string.
         * @name isString
         * @ngdoc method
         * @memberOf ihh-common-web.Utilities
         * @param {object} candidate the object to check
         * @returns {boolean} whether the object is a string
         */
        this.isString = function (candidate) {
            if (typeof candidate === 'string' || candidate instanceof String) {
                return true;
            }
            else {
                return false;
            }
        };

        /**
         * Executes the specified function whenever the event matches one of the specified keys.
         * @name doFnOnKeys
         * @ngdoc method
         * @memberOf ihh-common-web.Utilities
         * @param {object} $event the event to check against
         * @param {array} keys the keys to execute on
         * @param {function} fn the function to execute
         */
        this.doFnOnKeys = function ($event, keys, fn) {
            if (keys.indexOf($event.keyCode) > -1) {
                fn();
            }
        };

        /**
         * Closes the suggestions list created by ui.bootstrap.typeahead.
         * @name closeTypeaheadPopup
         * @ngdoc method
         * @memberOf ihh-common-web.Utilities
         */
        this.closeTypeaheadPopup = function (tagName) {
            // WARNING: hack below!
            var parentEl = angular.element(document.querySelector('[ng-model="' + 'tags.' + tagName + '"]'));
            var childId = parentEl.attr('aria-owns');
            var childEl = angular.element(document.querySelector('[id="' + childId + '"]'));

            childEl.addClass('ng-hide'); // this is what actually makes it work,
            // but prevents the suggestion list from being displayed
            // until some input is entered and removed entirely
            // if there were matches in the suggestion list

            //childEl.remove(); // yes, this works, but prevents the dropdown from being displayed ever again

            // the below seemingly have no effect, regardless of the $apply
            //parentEl.attr('aria-expanded', false);
            //childEl.attr('aria-hidden', true);
            //childEl.attr('ng-show', false);
            //$timeout(function () {
            //    $scope.$apply();
            //});

            // WARNING: end hack!
        };

        /**
         * Opens a modal dialog.
         * @name modal
         * @ngdoc method
         * @memberOf ihh-common-web.Utilities
         * @param {string} templateUrl the URL of the dialog template
         * @param {string} controller the name of the controller
         * @param {object} resolve an object for passing data into the controller
         * @param {function} onClose a function to execute on close
         * @param {function} onDismiss a function to execute on dismiss
         * @returns {object} the modal instance, which can be used to close or cancel it
         */
        this.modal = function (templateUrl, controller, resolve, onClose, onDismiss) {
            var modalInstance = $modal.open({
                templateUrl: templateUrl,
                controller: controller,
                resolve: resolve
            });

            onClose = onClose ||
                function () { };
            onDismiss = onDismiss ||
                function () { };

            modalInstance.result.then(onClose, onDismiss);

            return modalInstance;
        };

        /**
         * Opens a modal dialog and waits for the user to acknowledge.
         * @name modalWait
         * @ngdoc method
         * @memberOf ihh-common-web.Utilities
         * @param {string} message the message to show the user (defaults to "Loading...")
         * @param {string} buttonText the text to put on the button to dismiss the dialog (defaults to "Cancel")
         * @param {object} title the title for the dialog
         */
        this.modalWait = function (message, buttonText, title) {
            buttonText = buttonText || 'Cancel';
            return $modal.open({
                templateUrl: 'utilities/modal-wait.tpl.html',
                controller: 'ModalWaitCtrl',
                resolve: {
                    message: function () {
                        return message;
                    },
                    buttonText: function () {
                        return buttonText;
                    },
                    title: function () {
                        return title;
                    }
                }
            });
        };

        /**
         * Opens a modal dialog and waits for the user to pick a valid target for the exercise to be added
         * @name modalWait
         * @ngdoc method
         * @memberOf ihh-common-web.Utilities
         * @param {String} message the message to show the user (defaults to "Loading...")
         * @param {String} buttonText the text to put on the button to dismiss the dialog (defaults to "Cancel")
         * @param {Function} callback - function to execute when target is selected
         * @param {Function} retrieveValidTargets - the function to determine valid targets for an exercise
         * @returns {undefined}
         */
        this.modalExerciseQuickView = function (event, item, callback, retrieveValidTargets) {
            event.stopPropagation();
            var modalInstance = $modal.open({
                templateUrl: 'utilities/modal-exercise-quick-view.tpl.html',
                controller: 'ModalExerciseQuickViewCtrl',
                resolve: {
                    retrieveValidTargets: function () {
                        return retrieveValidTargets;
                    },
                    selectedItem: function () {
                        return item;
                    }
                }
            });

            modalInstance.result.then(function (result) {
                callback(result);
            });
        };

        /**
         * Opens a modal confirmation dialog, with the controller {@link ihh-common-web.ConfirmDialogCtrl}. The template should
         * contain buttons that call <code>ok()</code> if the user confirms or <code>cancel()</code> if the user cancels.
         * @memberOf ihh-common-web.Utilities
         * @name confirmDialog
         * @param {String} message the message to display in the confirm dialog
         * @param {Function} doit the function to call if the dialog is confirmed by the user
         */
        this.confirmDialog = function (message, doit, dontdoit, title, yesNoVariation) {
            var modalInstance = $modal.open({
                templateUrl: 'utilities/modal-confirm.tpl.html',
                controller: 'ModalConfirmCtrl',
                resolve: {
                    message: function () {
                        return message;
                    },
                    title: function () {
                        return title;
                    },
                    yesNoVariation: function () {
                        return yesNoVariation;
                    }
                }
            });

            if (doit || dontdoit) {
                modalInstance.result.then(doit, dontdoit);
            }
        };

        /**
         * Opens a modal text input dialog.
         * @memberOf ihh-common-web.Utilities
         * @name textInputDialog
         * @param {String} title the title to display in the dialog
         * @param {String} message an optional message to display above the input
         * @param {String} initialValue the initial value to populate in the text input
         * @param {String} placeholder placeholder text for the input field
         * @param {Function} callback the function to call with the entered text if the dialog is confirmed
         * @param {Function} cancelCallback optional function to call if the dialog is cancelled
         */
        this.textInputDialog = function (title, message, initialValue, placeholder, callback, cancelCallback) {
            var modalInstance = $modal.open({
                templateUrl: 'utilities/modal-text-input.tpl.html',
                controller: 'ModalTextInputCtrl',
                resolve: {
                    message: function () {
                        return message;
                    },
                    title: function () {
                        return title;
                    },
                    initialValue: function () {
                        return initialValue;
                    },
                    placeholder: function () {
                        return placeholder;
                    }
                }
            });

            if (callback || cancelCallback) {
                modalInstance.result.then(callback, cancelCallback);
            }
        };

        /**
         * Opens a modal dialog, with the controller {@link ihh-common-web.ModalNotifyCtrl}. The template should
         * contain buttons that call <code>ok()</code> if the user confirms.
         * @memberOf ihh-common-web.Utilities
         * @name notifyAway
         * @param {String} message the message to display in the confirm dialog
         * @param {Function} action the function to call if the dialog is confirmed by the user
         * @param {String} title the title to display in the confirm dialog
         */
        this.notifyAway = function (message, action, title, confirm) {
            var modalInstance = $modal.open({
                templateUrl: 'utilities/modal-notify.tpl.html',
                controller: 'ModalNotifyCtrl',
                resolve: {
                    message: function () {
                        return message;
                    },
                    title: function () {
                        return title;
                    },
                    confirm: function () {
                        return confirm;
                    }
                }
            });

            if (action) {
                modalInstance.result.then(action);
            }
        };

        /**
         * Watches for navigating away, and if the passed form is dirty, requires the user to confirm before leaving.
         * contain buttons that call <code>ok()</code> if the user confirms or <code>cancel()</code> if the user cancels.
         * @memberOf ihh-common-web.Utilities
         * @name confirmAway
         * @param {Object} $scope the scope used to register for the location change event
         * @param {Form} form the form to check for dirty
         * @param {String} message the message to display in the modal
         */
        this.confirmAway = function ($scope, form, message) {
            var locationChangeStartWatch = $scope.$on('$locationChangeStart', function (event, next) {
                if (UserSvc.isLoggedIn($scope.user) && form.$dirty) {
                    event.preventDefault();
                    //close any open modals first
                    $modalStack.dismissAll();
                    // show a modal to choose whether to stay or go
                    var confirmDialogMessage = '';
                    if (angular.isString(message)) {
                        confirmDialogMessage = message;
                    }
                    else {
                        confirmDialogMessage = 'Edits you just made to this page are not saved. Are you sure you want to continue without saving them?';
                    }
                    svc.confirmDialog(confirmDialogMessage, function () {
                        form.$setPristine();
                        var path = next.slice(next.indexOf('#') + 1);
                        $location.url(path);
                        locationChangeStartWatch();
                    }, angular.noop, 'You Have Unsaved Changes');
                }
            });
        };

        this.randomString = function (length) {
            var chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

            var result = '';
            for (var i = 0; i < length; i++) {
                var index = Math.floor(Math.random() * chars.length);
                result += chars.substring(index, index + 1);
            }

            return result;
        };

        /**
         * Reports an error in a modal dialog with an OK button.
         * @memberOf ihh-common-web.Utilities
         * @name reportError
         * @param {string} msgPrefix a prefix prepended to the error message
         * @param {string} msg the error message
         */
        this.reportError = function (msgPrefix, msg) {
            this.modalWait(msgPrefix + ': ' + msg, 'OK');
        };

        /**
         * Returns true if the item is defined and non-null; return false otherwise
         * @param {Object} item
         * @returns {Boolean}
         */
        this.isDefinedAndNotNull = function (item) {
            return angular.isDefined(item) && item !== null;
        };

        /**
         * Returns true if the item is defined, non-null, non-empty; return false otherwise
         * @param {String} val
         * @returns {Boolean}
         */
        this.isStringDefinedNotNullNonEmpty = function (string) {
            var self = this;
            return (self.isDefinedAndNotNull(string) && string !== '');
        };

        /**
         * This function interpolates strings, given a template and an array
         * of strings.
         *
         * @param {string} templateString the template string to use
         * @param {array} strings the array of strings to insert
         * @returns {unresolved}
         */
        this.interpolateStrings = function (templateString, strings) {
            var formattedString = templateString;

            if (angular.isArray(strings)) { //could also check lastIndexOf {\d+} and strings.length
                angular.forEach(strings, function (string, index) {
                    formattedString = formattedString.replace('{' + index + '}', string);
                });
            }

            return formattedString;
        };

        /**
         * This function performs a deep copy on JSON serializable entities.
         * JSON stringify creates a JSON string from an object / array
         * JSON parse deserializes a JSON string into a javascript object.
         *
         * @param {Object} the object to perform a deep copy on
         * @returns {Object} the copied object
         */
        this.deepCopy = function (obj) {
            return JSON.parse(JSON.stringify(obj));
        };

        /**
         * Moves the user back one page in their history.
         *
         * @ngdoc method
         * @name back
         * @memberOf ihh-common-web.Utilities
         */
        this.back = function () {
            $window.history.back();
        };
    }
})();